import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForceUpdate } from '@beef/react';
import { ClassifyContext } from "./context";
import { StyleSSR } from "./StyleSSR";
import { serializeRules } from "../common/serializers";
var ClassifyState = /*#__PURE__*/function (ClassifyState) {
  ClassifyState[ClassifyState["AWAITING_HYDRATION"] = 0] = "AWAITING_HYDRATION";
  ClassifyState[ClassifyState["AWAITING_UPDATE"] = 1] = "AWAITING_UPDATE";
  ClassifyState[ClassifyState["SCHEDULED_UPDATE"] = 2] = "SCHEDULED_UPDATE";
  ClassifyState[ClassifyState["TRIGGERED_UPDATE"] = 3] = "TRIGGERED_UPDATE";
  return ClassifyState;
}(ClassifyState || {});
var useClassifyState = function useClassifyState() {
  var state = useRef(ClassifyState.AWAITING_HYDRATION);
  var forceUpdate = useForceUpdate();
  useEffect(function () {
    state.current = ClassifyState.AWAITING_UPDATE;
  });
  return useMemo(function () {
    return {
      isReady: function isReady() {
        return state.current === ClassifyState.AWAITING_UPDATE;
      },
      needsUpdate: function needsUpdate() {
        return state.current === ClassifyState.SCHEDULED_UPDATE;
      },
      scheduleUpdate: function scheduleUpdate() {
        state.current = ClassifyState.SCHEDULED_UPDATE;
      },
      triggerUpdate: function triggerUpdate() {
        state.current = ClassifyState.TRIGGERED_UPDATE;
        forceUpdate();
      }
    };
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
};
/**
 * Работает в трех режимах:
 * --SSR-- (window undefined):
 * 1. Агрегирует стили в ref в процессе рендеринга
 *    - дубликаты: перезаписывают?
 * 2. Выводит стили в style-тэг
 *
 * --Hydrate-- (window defined, IncrementalMode: false):
 * 1. Агрегирует стили в ref
 * 2. useLayoutEffect -> выводит все стили
 * 3. Переключает "IncrementalMode" в true
 *
 * --Rerender-- (window defined, IncrementalMode: true):
 * 1. Сравнивает стили, с уже имеющимися
 */

export var ClassifyCollector = function ClassifyCollector(_ref) {
  var children = _ref.children,
    classGenerator = _ref.classGenerator;
  var state = useClassifyState();
  var flushedClasses = useRef({});
  var createClass = useCallback(function (uniqueId, styles) {
    var generatedClassName = classGenerator(uniqueId);
    var newStyles = serializeRules(styles);
    var hasStyleChanges = newStyles !== flushedClasses.current[generatedClassName];
    if (hasStyleChanges && state.isReady()) {
      state.scheduleUpdate();
    }
    flushedClasses.current[generatedClassName] = newStyles;
    return newStyles ? generatedClassName : undefined;
  }, [classGenerator, state]);
  var onUpdate = useCallback(function () {
    if (state.needsUpdate()) state.triggerUpdate();
  }, [state]);
  return /*#__PURE__*/React.createElement(ClassifyContext.Provider, {
    value: useMemo(function () {
      return {
        createClass: createClass,
        onUpdate: onUpdate
      };
    }, [createClass, onUpdate])
  }, children, /*#__PURE__*/React.createElement(StyleSSR, null, flushedClasses.current));
};